/**
 * Floating captions over the board (docs/ux.md U-LOG-04, U-FIRE-09): "Hit! 2/3", "Sunk!",
 * "Wrecked", "Repaired" above the unit, for a couple of seconds, following the camera.
 */
import { useSyncExternalStore } from 'react';
import type { Camera } from '../board/camera.ts';
import { CELL } from '../board/geometry.ts';
import { cellScreen } from '../board/focus.ts';
import type { ProjectionKind } from '../board/projection.ts';
import type { Caption } from './captions.ts';
import * as styles from './Game.css.ts';

/** A ship's mastheads over the middle of its cell, in cells. */
const MASTHEAD = 0.75;

export function BoardCaptions({
  captions,
  camera,
  projection,
}: {
  captions: readonly Caption[];
  camera: Camera;
  projection: ProjectionKind;
}) {
  // Re-render as the camera moves, so the captions stay over their units.
  const { zoom } = useSyncExternalStore(camera.subscribe, camera.getSnapshot);
  // Over the mastheads, not on the hull: a ship stands about this high over its cell's middle.
  const lift = CELL * zoom * MASTHEAD;
  return (
    <div className={styles.captions} aria-hidden="true">
      {captions.map((c) => {
        const p = cellScreen(camera, projection, c.cell);
        return (
          <span
            key={c.id}
            className={styles.caption}
            data-tone={c.tone}
            style={{ left: p.x, top: p.y - lift }}
          >
            {c.text}
          </span>
        );
      })}
    </div>
  );
}
